import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Camera, ChevronLeft, ChevronRight, CheckCheck, Loader2, Wifi, WifiOff, RefreshCw, AlertCircle, ListChecks, Mic } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import DefectSheet from "../../components/DefectSheet";
import FloatingSummary from "../../components/FloatingSummary";
import ReinspectionPanel from "../../components/ReinspectionPanel";
import { SevBadge } from "../../components/Bits";
import { api } from "../../lib/api";
import { useInspection } from "../../lib/offline";
import { liveSummary, worstSeverity, SEV } from "../../lib/summary";

const SYNC = { SYNCED: ["SYNCED", "bg-emerald-50 text-emerald-700 border-emerald-200", Wifi], SYNCING: ["SYNCING", "bg-amber-50 text-amber-700 border-amber-200", RefreshCw],
  OFFLINE: ["OFFLINE · saved locally", "bg-slate-900 text-white border-slate-900", WifiOff], ERROR: ["RETRY PENDING", "bg-rose-50 text-rose-700 border-rose-200", AlertCircle] };
const TESTER = ["NORMAL", "REVERSE POLARITY", "LOW/ABNORMAL READING", "NO POWER", "NOT TESTED"];

export function SyncBadge({ status }) {
  const [l, c, I] = SYNC[status] || SYNC.SYNCED;
  return <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] font-bold tracking-wider ${c}`} data-testid="sync-status-badge"><I className={`h-3 w-3 ${status === "SYNCING" ? "animate-spin" : ""}`} />{l}</span>;
}

const RESULTS = [["PASS", "PASS"], ["MINOR", "MINOR"], ["MAJOR", "MAJOR"], ["COSMETIC", "COSM"], ["NA", "N/A"], ["NT", "N/T"]];

function ItemRow({ it, defects, onResult, onDefect, onEdit, onTester }) {
  return (
    <div className="border-t border-slate-100 py-3 first:border-t-0" data-testid={`item-${it.item_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-medium">{it.name}</span>
        <div className="flex items-center gap-2">
          {it.result && <SevBadge sev={it.result} testid={`item-result-${it.item_id}`} />}
          <button onClick={() => onDefect(it, "MINOR", true)} className="tap grid h-10 w-10 place-items-center rounded-md bg-slate-900 text-teal-300" data-testid={`item-photo-${it.item_id}`} title="Take photo"><Camera className="h-4 w-4" /></button>
        </div>
      </div>
      {it.tester !== null && it.tester !== undefined && (
        <div className="mt-2 flex flex-wrap gap-1">
          {TESTER.map((t) => <button key={t} onClick={() => onTester(it, t)} className={`tap rounded border px-2 py-1 font-mono text-[10px] font-bold ${it.tester === t ? (t === "NORMAL" ? "border-emerald-600 bg-emerald-600 text-white" : t === "NOT TESTED" ? "border-slate-700 bg-slate-700 text-white" : "border-rose-600 bg-rose-600 text-white") : "border-slate-200 text-slate-500"}`} data-testid={`item-tester-${it.item_id}-${t.split(/[ /]/)[0].toLowerCase()}`}>{t}</button>)}
        </div>
      )}
      <div className="mt-2 grid grid-cols-6 gap-1">
        {RESULTS.map(([k, l]) => (
          <button key={k} onClick={() => (["MINOR", "MAJOR", "COSMETIC"].includes(k) ? onDefect(it, k, false, true) : onResult(it, it.result === k ? null : k))}
            className={`tap h-11 rounded-md border text-[11px] font-bold ${it.result === k ? SEV[k].solid : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`} data-testid={`item-${k.toLowerCase()}-${it.item_id}`}>{l}</button>
        ))}
      </div>
      {defects.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {defects.map((d) => <button key={d.defect_id} onClick={() => onEdit(d)} className={`tap rounded border px-2 py-1 text-xs font-semibold ${SEV[d.severity].cls}`} data-testid={`item-defect-chip-${d.number}`}>#{d.number} {d.name}{d.photos?.length ? ` · ${d.photos.length} photo` : ""}</button>)}
          <button onClick={() => onDefect(it, "MINOR")} className="tap rounded border border-dashed border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600" data-testid={`item-add-another-${it.item_id}`}>+ defect</button>
        </div>
      )}
    </div>
  );
}

function RoomList({ ins, s, onOpen }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="room-list">
      {s.rooms.map((r, i) => (
        <button key={r.room_id} onClick={() => onOpen(r.room_id)} className="tap rounded-lg border border-slate-200 bg-white p-4 text-left hover:border-slate-900" data-testid={`room-card-${i}`}>
          <div className="flex items-center justify-between">
            <span className="font-semibold"><span className="mr-2 font-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>{r.name}</span>
            <span className={`font-mono text-[10px] font-bold ${r.progress === 100 ? "text-emerald-600" : r.progress ? "text-amber-600" : "text-slate-400"}`}>{r.progress === 100 ? "COMPLETE" : r.progress ? `${r.progress}%` : "NOT STARTED"}</span>
          </div>
          <div className="mt-3 h-1.5 rounded bg-slate-100"><div className={`h-full rounded ${r.progress === 100 ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${r.progress}%` }} /></div>
          <div className="mt-2 flex gap-3 font-mono text-[10px] text-slate-500"><span>{r.done}/{r.total} items</span>{r.major > 0 && <span className="text-rose-600">{r.major} MAJ</span>}{r.minor > 0 && <span className="text-amber-600">{r.minor} MIN</span>}{r.cosmetic > 0 && <span className="text-blue-600">{r.cosmetic} COS</span>}</div>
        </button>
      ))}
    </div>
  );
}

export default function Inspect() {
  const { iid } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const roomId = sp.get("room");
  const { ins, update, status, forceOffline, toggleOffline } = useInspection(iid);
  const [library, setLibrary] = useState([]);
  const [sheet, setSheet] = useState(null);
  useEffect(() => { api.get("/defects").then(({ data }) => setLibrary(data)); }, []);
  const s = useMemo(() => (ins ? liveSummary(ins) : null), [ins]);
  if (!ins) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const room = ins.rooms.find((r) => r.room_id === roomId);
  const idx = ins.rooms.findIndex((r) => r.room_id === roomId);
  const isRe = ins.type === "reinspection";
  const setRoom = (id) => { setSp(id ? { room: id } : {}); window.scrollTo(0, 0); };
  const patchItem = (rid, itemId, patch) => update((x) => ({ ...x, rooms: x.rooms.map((r) => (r.room_id !== rid ? r : { ...r, items: r.items.map((i) => (i.item_id === itemId ? { ...i, ...patch } : i)) })) }));
  const recalc = (x, itemId, rid) => {
    if (!itemId) return x.rooms;
    const ds = x.defects.filter((d) => d.item_id === itemId);
    return x.rooms.map((r) => (r.room_id !== rid ? r : { ...r, items: r.items.map((i) => (i.item_id === itemId ? { ...i, result: ds.length ? worstSeverity(ds) : null } : i)) }));
  };
  const saveDefect = (d) => {
    update((x) => {
      const exists = x.defects.some((y) => y.defect_id === d.defect_id);
      const nx = { ...x, defects: exists ? x.defects.map((y) => (y.defect_id === d.defect_id ? d : y)) : [...x.defects, d] };
      nx.rooms = recalc(nx, d.item_id, d.room_id);
      if (d.tester_result && d.item_id) nx.rooms = nx.rooms.map((r) => ({ ...r, items: r.items.map((i) => (i.item_id === d.item_id && i.tester !== null && i.tester !== undefined ? { ...i, tester: d.tester_result } : i)) }));
      return nx;
    });
    setSheet(null);
  };
  const delDefect = (d) => { update((x) => { const nx = { ...x, defects: x.defects.filter((y) => y.defect_id !== d.defect_id) }; nx.rooms = recalc(nx, d.item_id, d.room_id); return nx; }); setSheet(null); };
  const completeRoom = () => {
    update((x) => ({ ...x, rooms: x.rooms.map((r) => (r.room_id !== roomId ? r : { ...r, items: r.items.map((i) => (i.result ? i : { ...i, result: "PASS", tester: i.tester === "NOT TESTED" ? "NORMAL" : i.tester })) })) }));
    if (idx < ins.rooms.length - 1) setRoom(ins.rooms[idx + 1].room_id); else nav(`/inspector/inspection/${iid}/summary`);
  };
  const cats = room ? [...new Set(room.items.map((i) => i.category))] : [];
  const rs = room && s.rooms.find((r) => r.room_id === roomId);

  return (
    <div className="mx-auto max-w-5xl pb-28" data-testid="inspection-screen">
      <div className="sticky top-14 z-20 -mx-4 mb-4 border-b border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono text-[10px] text-slate-500" data-testid="inspection-id">{ins.inspection_id} · {ins.property.project} {ins.property.flat}</div>
            <div className="flex items-center gap-2">
              {room && <button onClick={() => setRoom(null)} className="tap -ml-1 rounded p-1 hover:bg-slate-200" data-testid="back-to-rooms-button"><ChevronLeft className="h-5 w-5" /></button>}
              <h1 className="truncate font-display text-2xl font-extrabold uppercase" data-testid="current-room-title">{room ? room.name : isRe ? "Re-inspection" : "Select area / room"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SyncBadge status={status} />
            <label className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500">OFFLINE<Switch checked={forceOffline} onCheckedChange={toggleOffline} data-testid="simulate-offline-switch" /></label>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="h-2 flex-1 rounded bg-slate-200"><div className="h-full rounded bg-teal-500 transition-all" style={{ width: `${room ? rs.progress : s.progress}%` }} /></div>
          <span className="font-mono text-xs font-bold" data-testid="progress-percent">{room ? `${rs.progress}%` : `Overall ${s.progress}%`}</span>
        </div>
      </div>

      {!room && !isRe && (
        <>
          <div className="mb-4 grid grid-cols-4 gap-2 sm:grid-cols-7" data-testid="progress-stats">
            {[["Items", s.items.total], ["Passed", s.items.PASS], ["Major", s.items.MAJOR], ["Minor", s.items.MINOR], ["Cosmetic", s.items.COSMETIC], ["Not tested", s.items.NT], ["N/A", s.items.NA]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-white px-2 py-2 text-center"><div className="font-display text-lg font-extrabold">{v}</div><div className="font-mono text-[9px] uppercase text-slate-500">{l}</div></div>
            ))}
          </div>
          <RoomList ins={ins} s={s} onOpen={setRoom} />
        </>
      )}
      {!room && isRe && <ReinspectionPanel ins={ins} update={update} onNewDefect={() => setSheet({ roomId: ins.rooms[0]?.room_id, item: null })} />}

      {room && (
        <div className="space-y-3">
          {cats.map((c) => {
            const items = room.items.filter((i) => i.category === c);
            const pending = items.filter((i) => !i.result);
            return (
              <section key={c} className="rounded-lg border border-slate-200 bg-white px-4 pb-2 pt-3" data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-bold tracking-[0.18em] text-teal-700">{c.toUpperCase()}</span>
                  <div className="flex gap-1.5">
                    {pending.length > 0 && <button onClick={() => pending.forEach((i) => patchItem(room.room_id, i.item_id, { result: "PASS" }))} className="tap h-9 rounded-md bg-emerald-600 px-3 text-xs font-bold text-white" data-testid={`category-pass-all-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}><CheckCheck className="mr-1 inline h-3.5 w-3.5" />PASS {pending.length}</button>}
                    <button onClick={() => setSheet({ roomId: room.room_id, item: items[0], defaultSev: "MINOR" })} className="tap h-9 rounded-md bg-slate-900 px-3 text-xs font-bold text-white" data-testid={`category-add-defect-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>ADD DEFECT</button>
                  </div>
                </div>
                {items.map((it) => (
                  <ItemRow key={it.item_id} it={it} defects={ins.defects.filter((d) => d.item_id === it.item_id)}
                    onResult={(i, r) => patchItem(room.room_id, i.item_id, { result: r })}
                    onTester={(i, t) => patchItem(room.room_id, i.item_id, { tester: t })}
                    onDefect={(i, sev, autoPhoto, explicit) => setSheet({ roomId: room.room_id, item: i, defaultSev: sev, autoPhoto, sevExplicit: explicit })}
                    onEdit={(d) => setSheet({ roomId: room.room_id, item: it, defect: d })} />
                ))}
              </section>
            );
          })}
        </div>
      )}

      <FloatingSummary ins={ins} summary={s} onOpenDefect={(d) => { const r = ins.rooms.find((x) => x.room_id === d.room_id); setSheet({ roomId: d.room_id, item: r?.items.find((i) => i.item_id === d.item_id) || null, defect: d }); }}>
        {room ? (
          <>
            <button onClick={() => setSheet({ roomId: room.room_id, item: null, autoVoice: true })} className="tap grid h-11 w-11 place-items-center rounded-md bg-slate-800 text-teal-300" data-testid="room-voice-defect-button" title="Voice defect"><Mic className="h-5 w-5" /></button>
            <button disabled={idx <= 0} onClick={() => setRoom(ins.rooms[idx - 1].room_id)} className="tap grid h-11 w-11 place-items-center rounded-md border border-slate-700 disabled:opacity-30" data-testid="prev-room-button"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={completeRoom} className="tap flex h-11 items-center gap-1.5 rounded-md bg-teal-500 px-4 text-sm font-bold text-slate-950" data-testid="complete-room-button">COMPLETE ROOM <ChevronRight className="h-4 w-4" /></button>
          </>
        ) : (
          <Link to={`/inspector/inspection/${iid}/summary`} className="tap flex h-11 items-center gap-1.5 rounded-md bg-teal-500 px-4 text-sm font-bold text-slate-950" data-testid="final-summary-button"><ListChecks className="h-4 w-4" />FINAL SUMMARY</Link>
        )}
      </FloatingSummary>

      <DefectSheet open={!!sheet} onClose={() => setSheet(null)} ins={ins} library={library} roomId={sheet?.roomId} item={sheet?.item} defaultSev={sheet?.defaultSev} sevExplicit={sheet?.sevExplicit}
        defect={sheet?.defect} autoPhoto={sheet?.autoPhoto} autoVoice={sheet?.autoVoice} onSave={saveDefect} onDelete={delDefect} />
    </div>
  );
}
